/**
 * Pure helpers for Dev Team prompt template ↔ auto-binding form (unit-tested).
 */

export type BindingToastOp = 'template_created' | 'template_updated';

/** Canonical lesson-type tokens for optional `lesson_type_rule_map` on prompt bindings (any tenant). */
export const LESSON_TYPE_RULE_MAP_OPTION_ROWS = [
  { canonical: 'STORY', label: 'Story' },
  { canonical: 'RHYMES', label: 'Rhymes' },
  { canonical: 'VOCAB', label: 'Vocab' },
  { canonical: 'PESUVOM', label: 'Pesuvom' },
  { canonical: 'PRONUNCIATION', label: 'Pronunciation' },
  { canonical: 'GRAMMAR', label: 'Grammar' },
  { canonical: 'BODY_PARTS', label: 'Body parts' },
  { canonical: 'INTERACTIVE_ORAL', label: 'Interactive oral' },
] as const;

export type LessonTypeRuleCanonical = (typeof LESSON_TYPE_RULE_MAP_OPTION_ROWS)[number]['canonical'];

export type LessonTypeRuleSlotState = Record<LessonTypeRuleCanonical, string>;

export function emptyLessonTypeRuleSlots(): LessonTypeRuleSlotState {
  return Object.fromEntries(LESSON_TYPE_RULE_MAP_OPTION_ROWS.map((r) => [r.canonical, ''])) as LessonTypeRuleSlotState;
}

/** Build API `lesson_type_rule_map` from per-type dropdowns; omit field when nothing selected. */
export function buildLessonTypeRuleMapFromSlots(slots: LessonTypeRuleSlotState): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const row of LESSON_TYPE_RULE_MAP_OPTION_ROWS) {
    const v = slots[row.canonical]?.trim();
    if (v) out[row.canonical] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

export function validateAutoBindingForm(params: {
  tenantId: string;
  allowGlobalBinding: boolean;
  bindingPriority: string;
}): string | null {
  if (!params.allowGlobalBinding && !params.tenantId.trim()) {
    return 'Tenant ID is required unless “Allow global binding” is checked.';
  }
  const pri = Number(params.bindingPriority);
  if (!Number.isFinite(pri) || pri < 1 || !Number.isInteger(pri)) {
    return 'Binding priority must be a positive whole number.';
  }
  return null;
}

export function messageForBindingOutcome(
  op: BindingToastOp,
  bindingStatus: string | null | undefined,
  bindingReason?: string | null
): string {
  const reason = bindingReason?.trim();
  const suffix = reason ? ` (${reason})` : '';

  if (!bindingStatus) {
    return op === 'template_created' ? 'Template saved.' : 'Template updated.';
  }

  if (bindingStatus === 'skipped') {
    return op === 'template_created'
      ? `Template saved; binding skipped${suffix}`
      : `Template updated; binding skipped${suffix}`;
  }
  if (bindingStatus === 'created') {
    return op === 'template_created'
      ? `Template created and binding created${suffix}`
      : `Template updated and binding created${suffix}`;
  }
  if (bindingStatus === 'updated') {
    return op === 'template_created'
      ? `Template created and existing binding updated${suffix}`
      : `Template updated and existing binding updated${suffix}`;
  }
  return op === 'template_created'
    ? `Template saved · binding ${bindingStatus}${suffix}`
    : `Template updated · binding ${bindingStatus}${suffix}`;
}
